import { Injectable } from '@angular/core';
import { DataFarmType, DataFarmTypeUtil } from 'src/app/domainObjects/dataFarmType';
import { ConfigService } from '../config/config.service';
import { RunConfigRequest } from '../../messaging/runConfigRequest';
import { DataConnectorMapping } from 'src/app/domainObjects/dataConnectorMapping';
import { DataSourceType } from 'src/app/domainObjects/dataSourceType';
import { TransformerViewModel } from '../../views/transformer-list/transformer-list.component';
import { DataTransformerConfig } from 'src/app/domainObjects/dataTransformConfig';
import { PreviewDataImportResponse } from 'src/app/messaging/previewDataResponse';
import { Edge } from '@swimlane/ngx-graph';
import { UserDataService } from 'src/app/services/user-data-service';
import { CacheKeyType } from 'src/app/domainObjects/transformType';

class TransformExecutionResult {
  hasError: boolean;
  cacheChanged: boolean;

  constructor(hasError: boolean, cacheChanged: boolean) {
    this.hasError = hasError;
    this.cacheChanged = cacheChanged;
  }
}

export class GroupedTransforms {
  groupName: string;
  index: number;
  transforms: TransformerViewModel[] = [];
}

export class TransformPipeline {
  transforms: TransformerViewModel[] = [];
  allTheTransformsLists: TransformerViewModel[] = [];
  groupedTransforms: GroupedTransforms[] = [];

  insertTransform(index: number, transform: TransformerViewModel): void {
    this.transforms.splice(index, 0, transform);
  }

  // returns all the transformers downstream from the one provided
  getDownstreamTransforms(transform: TransformerViewModel): TransformerViewModel[] {
    // console.log('transform - getDownstreamTransforms in transform-pipeline.service', transform)

    // const index = this.transforms.indexOf(transform);
    const index = transform?.index || 0;
    // console.log('index - getDownstreamTransforms in transform-pipeline.service', index)

    // console.log('this.transforms - getDownstreamTransforms inut transform-pipeline.service', this.allTheTransformsLists)

    return this.subListFromRange<TransformerViewModel>(this.allTheTransformsLists, index , this.allTheTransformsLists.length-1 );
  }

  subListFromRange<T>(list: T[], start: number, end: number): T[] {
    const subList: T[] = [];
    for (let i = start; i <= end; i++) {
      subList.push(list[i]);
    }

    return subList;
  }
}

const delay = (ms) => new Promise(res => setTimeout(res, ms));

type TransformRequestBuilder = ( viewModel: TransformerViewModel) => RunConfigRequest;

@Injectable({
  providedIn: 'root'
})
export class TransformPipelineService {

  constructor(private configService: ConfigService, private userData: UserDataService) { }

  replacer = (key, value) =>
    value instanceof Object && !(value instanceof Array) ?
      Object.keys(value)
      .sort()
      .reduce((s, k) => {
        s[k] = value[k];
        return s;
      }, {}) : value

  async executePipelineAsync(
    refineryID: string,
    parentNodes: Edge[],
    AttributeNodes:  Edge[],
    pipeline: TransformPipeline,
    activeIngestFarmId: string,
    transformerRunType: string,
    activeTransformGuid: string, 
    onTransformStarted?: (transform: TransformerViewModel, groupName: string) => void,
    onBeforeExecuteRequest?: (request: RunConfigRequest) => void,
    onTransformCompleted?: (transform: TransformerViewModel, groupName: string, success: boolean, response?: any, err?: Error) => void,
    requestBuilder?: TransformRequestBuilder): Promise<boolean> {
    let abort = false;
    let previousTransform: TransformerViewModel;
    let forceRun = false; // determine if next transform should run again because upstream transform updated it's cache
    
    for (const group of pipeline.groupedTransforms) {
      if (abort) {
        break;
      }
      for (const transformerViewModel of group.transforms) {
        if (abort) {
          break;
        }
        transformerViewModel.transform.inputTransformGuid = previousTransform?.guid;
        onTransformStarted(transformerViewModel, group.groupName);
        const index = pipeline.transforms.indexOf(transformerViewModel);
        const downstreamTransforms = pipeline.getDownstreamTransforms(transformerViewModel);
        const downstreamTransformGuids = downstreamTransforms?downstreamTransforms.map(t => t.guid) : [];
        const result = await executeTransform(
          refineryID,
          parentNodes,
          AttributeNodes,
          this.configService,
          this.userData,
          group.groupName,
          transformerViewModel,
          activeIngestFarmId,
          forceRun,
          downstreamTransformGuids,
          transformerRunType,
          activeTransformGuid,
          onBeforeExecuteRequest,
          onTransformCompleted,
          requestBuilder);
        
        abort = result.hasError;
        forceRun = result.cacheChanged;
        // Track the prior transform so we can use this as input to next Transform
        previousTransform = transformerViewModel;
      }
    }

    return Promise.resolve(!abort);
  }

  async executePipelineAsyncOld(
    refineryID: string,
    parentNodes: Edge[],
    AttributeNodes:  Edge[],
    pipeline: TransformPipeline,
    dataConnectors: DataConnectorMapping[],
    activeIngestFarmId: string,
    transformerRunType: string,
    activeTransformGuid: string, 
    onTransformStarted?: (transform: TransformerViewModel, groupName: string) => void,
    onBeforeExecuteRequest?: (request: RunConfigRequest) => void,
    onTransformCompleted?: (transform: TransformerViewModel, groupName: string, success: boolean, response?: any, err?: Error) => void,
    requestBuilder?: TransformRequestBuilder): Promise<boolean> {
    let abort = false;
    let previousTransform: TransformerViewModel;
    let forceRun = false; // determine if next transform should run again because upstream transform updated it's cache
    
    for (const group of pipeline.groupedTransforms) {
      if (abort) {
        break;
      }

      for (const transformerViewModel of group.transforms) {
        if (abort) {
          break;
        }

        transformerViewModel.transform.inputTransformGuid = previousTransform?.guid;

        onTransformStarted(transformerViewModel, group.groupName);
        // console.log('transformerViewModel in transform-pipeline.service', transformerViewModel)

        const index = pipeline.transforms.indexOf(transformerViewModel);
        
        const downstreamTransforms = pipeline.getDownstreamTransforms(transformerViewModel);
        // console.log('downstreamTransforms in transform-pipeline.service', downstreamTransforms)

        const downstreamTransformGuids = downstreamTransforms?downstreamTransforms.map(t => t.guid) : [];
        


        // const downstreamTransformGuids = pipeline.getDownstreamTransforms(transformerViewModel).map(t => t.guid);

        // console.log('downstreamTransformGuids in transform-pipeline.service', downstreamTransformGuids)

        const result = await executeTransformOld(
          refineryID,
          parentNodes,
          AttributeNodes,
          this.configService,
          this.userData,
          group.groupName,
          transformerViewModel,
          activeIngestFarmId,
          forceRun,
          downstreamTransformGuids,
          transformerRunType,
          activeTransformGuid,
          dataConnectors,
          onBeforeExecuteRequest,
          onTransformCompleted,
          requestBuilder);
        
        abort = result.hasError;
        forceRun = result.cacheChanged;

        // Track the prior transform so we can use this as input to next Transform
        previousTransform = transformerViewModel;
      }
    }

    return Promise.resolve(!abort);
  }

  buildPipeline(currentTransformGuid: string, allTransforms: TransformerViewModel[]): TransformPipeline {
    const pipeline = new TransformPipeline();
    const ingestTransforms = allTransforms.filter(t => t.transform.dataFarmType === DataFarmType.ingest);
    const subTaskTransforms = allTransforms.filter(t => t.transform.dataFarmType === DataFarmType.subTask);
    const taskTransforms = allTransforms.filter(t => t.transform.dataFarmType === DataFarmType.task);
    const activityTransforms = allTransforms.filter(t => t.transform.dataFarmType === DataFarmType.activity);
    const currentTransform = allTransforms.filter(t => t.guid === currentTransformGuid)[0];
    const currentDataFarmType = currentTransform?.transform.dataFarmType;

    pipeline.allTheTransformsLists = allTransforms;
    pipeline.allTheTransformsLists.sort((a, b) => a.index - b.index);

    // console.log('allTransforms of buildPipeline in Transform-pipeline.ts', pipeline.allTheTransformsLists)
    //console.log('currentTransform of buildPipeline in Transform-pipeline.ts', currentTransform)

    switch (currentDataFarmType) {
      case DataFarmType.ingest:
        this.addTransforms(currentDataFarmType, pipeline, ingestTransforms, currentTransform);
        break;
      case DataFarmType.subTask:
        this.addTransforms(DataFarmType.ingest, pipeline, ingestTransforms, currentTransform);
        this.addTransforms(DataFarmType.subTask, pipeline, subTaskTransforms, currentTransform);
        break;
      case DataFarmType.task:
        this.addTransforms(DataFarmType.ingest, pipeline, ingestTransforms, currentTransform);
        this.addTransforms(DataFarmType.subTask, pipeline, subTaskTransforms, currentTransform);
        this.addTransforms(DataFarmType.task, pipeline, taskTransforms, currentTransform);
        break;
      case DataFarmType.activity:
        this.addTransforms(DataFarmType.ingest, pipeline, ingestTransforms, currentTransform);
        this.addTransforms(DataFarmType.subTask, pipeline, subTaskTransforms, currentTransform);
        this.addTransforms(DataFarmType.task, pipeline, taskTransforms, currentTransform);
        this.addTransforms(DataFarmType.activity, pipeline, activityTransforms, currentTransform);
        break;
    }

    return pipeline;
  }

  private addTransforms(
    farmType: DataFarmType,
    pipeline: TransformPipeline,
    transforms: TransformerViewModel[],
    currentTransform: TransformerViewModel): void {

    const groupedTransform = new GroupedTransforms();
    groupedTransform.index = 0;
    groupedTransform.groupName = DataFarmTypeUtil.toString(farmType);
    pipeline.groupedTransforms.push(groupedTransform);

    transforms.sort((a, b) => a.index - b.index);

    const ingestIndex = transforms.indexOf(currentTransform, 0);

    //console.log('farmType of addTransforms in Transform-pipeline.ts', farmType)
    //console.log('transforms of addTransforms in Transform-pipeline.ts', transforms)
    //console.log('groupedTransform of addTransforms in Transform-pipeline.ts', groupedTransform)


    // tslint:disable-next-line:prefer-for-of
    for (let i = 0; i < transforms.length; i++) {
      if (i > ingestIndex) {
        break;
      }
      pipeline.insertTransform(i, transforms[i]);
      groupedTransform.transforms.splice(i, 0, transforms[i]);
    }
    //console.log('groupedTransform end loop of addTransforms in Transform-pipeline.ts', groupedTransform)

  }
}

async function executeTransform(
  refineryID:string,
  parentNodes: Edge[],
  AttributeNodes: Edge[],
  configService: ConfigService,
  userDataService: UserDataService,
  groupName: string,
  transformerViewModel: TransformerViewModel,
  activeIngestFarmId: string,
  forceRun: boolean,
  downstreamTransformGuids: string[],
  transformerRunType: string,
  activeTransformGuid: string,
  onBeforeExecuteRequest?: (request: RunConfigRequest) => void,
  onTransformCompleted?: (transform: TransformerViewModel,
                          groupName: string,
                          success: boolean, response?: any, err?: Error) => void,
  requestBuilder?: TransformRequestBuilder): Promise<TransformExecutionResult> {
  // Make a deep copy of the transform so we can modify Transient properties during a run
  // which won't affect the actual transform instance used in the app
  const transientTransform = DataTransformerConfig.deserializeTransform(transformerViewModel.transform);
  const originalTransform = transformerViewModel.transform;
  transformerViewModel.transform = transientTransform;
  const builderRequest = requestBuilder != null ? requestBuilder(transformerViewModel) : null;
  const request = builderRequest ?? new RunConfigRequest();
  // allow using a cached Transforms output data frame instead of a dataConnector for this transforms input
  request.inputTransformGuid = transformerViewModel.transform.inputTransformGuid;
  request.refineryID = refineryID;
  request.activeIngestFarmId = activeIngestFarmId;
  request.downstreamTransformGuids = downstreamTransformGuids;
  request.DataConnectorNodes = parentNodes;
  request.AttrConnectorNodes = AttributeNodes;
  request.transformerRunType = transformerRunType;
  request.activeTransformId = activeTransformGuid;

  request.userId = userDataService.getUserId();
  request.orgId = userDataService.getOrgId();
  request.refineryId = userDataService.getRefineryId();
  request.nodeId = userDataService.getNodeId();
  request.transformId = userDataService.getTransformId();
  request.cacheKeyType = CacheKeyType.transformerOutput;
   
  transformerViewModel.transform.forceRun = false;

  if (!builderRequest) {
    request.transforms = [];
    request.transforms.push(transformerViewModel.transform);
    request.dataSourceType = DataSourceType.timeStampedData;  // TODO: Get this value from another source
    request.requireResults = false;
    
  }

  try {
    onBeforeExecuteRequest?.call(this, request);
    const response: PreviewDataImportResponse = await configService.runConfig(request); // TODO: Here it is runConfig to call Python 
    onTransformCompleted(transformerViewModel, groupName, true, response);
    transformerViewModel.transform = originalTransform;
    return new TransformExecutionResult(false, response.cacheUpdated);
  } catch (err) {
    transformerViewModel.transform = originalTransform;
    onTransformCompleted(transformerViewModel, groupName, false, null, err);
    return new TransformExecutionResult(true, false);
  }
}

async function executeTransformOld(
  refineryID:string,
  parentNodes: Edge[],
  AttributeNodes: Edge[],
  configService: ConfigService,
  userDataService: UserDataService,
  groupName: string,
  transformerViewModel: TransformerViewModel,
  activeIngestFarmId: string,
  forceRun: boolean,
  downstreamTransformGuids: string[],
  transformerRunType: string,
  activeTransformGuid: string,
  dataConnectors?: DataConnectorMapping[],
  onBeforeExecuteRequest?: (request: RunConfigRequest) => void,
  onTransformCompleted?: (transform: TransformerViewModel,
                          groupName: string,
                          success: boolean, response?: any, err?: Error) => void,
  requestBuilder?: TransformRequestBuilder): Promise<TransformExecutionResult> {
  // Make a deep copy of the transform so we can modify Transient properties during a run
  // which won't affect the actual transform instance used in the app
  const transientTransform = DataTransformerConfig.deserializeTransform(transformerViewModel.transform);
  const originalTransform = transformerViewModel.transform;
  transformerViewModel.transform = transientTransform;
  const builderRequest = requestBuilder != null ? requestBuilder(transformerViewModel) : null;
  const request = builderRequest ?? new RunConfigRequest();
  // allow using a cached Transforms output data frame instead of a dataConnector for this transforms input
  request.inputTransformGuid = transformerViewModel.transform.inputTransformGuid;
  request.refineryID = refineryID;
  request.activeIngestFarmId = activeIngestFarmId;
  request.downstreamTransformGuids = downstreamTransformGuids;
  request.dataConnectorMappings = dataConnectors;
  request.DataConnectorNodes = parentNodes;
  request.AttrConnectorNodes = AttributeNodes;
  request.transformerRunType = transformerRunType;
  request.activeTransformId = activeTransformGuid;

  request.userId = userDataService.getUserId();
  request.orgId = userDataService.getOrgId();
  request.refineryId = userDataService.getRefineryId();
  request.nodeId = userDataService.getNodeId();
  request.transformId = userDataService.getTransformId();
  request.cacheKeyType = CacheKeyType.transformerOutput;
   
  transformerViewModel.transform.forceRun = false;

  if (!builderRequest) {
    request.transforms = [];
    request.transforms.push(transformerViewModel.transform);
    request.dataSourceType = DataSourceType.timeStampedData;  // TODO: Get this value from another source
    request.requireResults = false;
    
  }

  try {
    onBeforeExecuteRequest?.call(this, request);
    const response: PreviewDataImportResponse = await configService.runConfig(request); // TODO: Here it is runConfig to call Python 
    onTransformCompleted(transformerViewModel, groupName, true, response);
    transformerViewModel.transform = originalTransform;
    return new TransformExecutionResult(false, response.cacheUpdated);
  } catch (err) {
    transformerViewModel.transform = originalTransform;
    onTransformCompleted(transformerViewModel, groupName, false, null, err);
    return new TransformExecutionResult(true, false);
  }
}

